// A Transaction Wizard embedded in a smart page (an `embed` block with
// `obj_type: "U"`, docs/plans/wizard-smart-page-embed.md) — the frame every
// host draws around its runner, so the platform (Run view, site viewer,
// published website) and the downloaded runtime behave the same:
//
//   • the run starts with the embed's launch inputs (its `args`, resolved by
//     the host against the page variables) and starts OVER when they change —
//     a different purchase order picked on the page is a different run;
//   • when the run ends (finish.then 'close', or the person cancels) the frame
//     shows a short "done" panel with Start again, writes
//     `{wizard, outcome, txn_id}` into the embed's `resultVar` so other blocks
//     can react, and tells the host (a modal presentation closes).
//
// The host owns the runner (`renderRunner`) — its transport, drawers and
// report preview — and nothing here talks to a server.

import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { CheckCircle2, RotateCcw, XCircle, Clock } from 'lucide-react'
import { tw } from '../testHooks'
import type { WizardCallOutcome } from './contract'
import { T, button } from './styles'

export interface WizardEmbedResult {
  wizard: string
  outcome: WizardCallOutcome
  txn_id: string | null
}

export interface WizardEmbedProps {
  wizard: string
  /** The launch inputs, already resolved from the embed's `args`. */
  inputs: Record<string, unknown>
  /** Mount the host's runner for one run; call `onClose` when it ends. */
  renderRunner: (run: {
    wizard: string
    inputs: Record<string, unknown>
    onClose: (result: { outcome: WizardCallOutcome; txnId?: string | null }) => void
  }) => ReactNode
  /** The json page variable that receives the result (`embed.resultVar`). */
  resultVar?: string
  onSetVar?: (name: string, value: unknown) => void
  /** The run ended — a modal presentation closes on it. */
  onFinished?: (result: WizardEmbedResult) => void
  /** Shown when the run completed (`embed.successMessage`). */
  successMessage?: string
  /** A fixed block height; otherwise the frame sizes to the step. */
  height?: string
}

const DONE_TEXT: Record<WizardCallOutcome, string> = {
  completed: 'Done — thank you.',
  cancelled: 'Cancelled. Nothing was saved.',
  pending_approval: 'Submitted for approval.',
  pending_assignee: 'Handed over — someone else will finish it.',
  failed: 'This could not be finished.',
}

export default function WizardEmbed({
  wizard, inputs, renderRunner, resultVar, onSetVar, onFinished, successMessage, height,
}: WizardEmbedProps) {
  const launchKey = JSON.stringify([wizard, inputs])
  // The host resolves `inputs` afresh on every render; the runner must see
  // the SAME object until the values change, or a host that restarts on a
  // new inputs object (the platform's) would start over on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const launchInputs = useMemo(() => inputs, [launchKey])
  const [run, setRun] = useState({ launchKey, n: 0 })
  const [done, setDone] = useState<WizardEmbedResult | null>(null)
  // New launch inputs start a fresh run (and forget a finished one).
  if (run.launchKey !== launchKey) {
    setRun({ launchKey, n: run.n + 1 })
    if (done) setDone(null)
  }

  const onClose = useCallback((r: { outcome: WizardCallOutcome; txnId?: string | null }) => {
    const result: WizardEmbedResult = { wizard, outcome: r.outcome, txn_id: r.txnId ?? null }
    setDone(result)
    if (resultVar && onSetVar) {
      try {
        onSetVar(resultVar, result)
      } catch (err) {
        // A broken page-variable sink must not hide the done panel.
        console.warn('[wizard embed] result variable not set', resultVar, err)
      }
    }
    onFinished?.(result)
  }, [wizard, resultVar, onSetVar, onFinished])

  const restart = () => {
    setDone(null)
    setRun((r) => ({ ...r, n: r.n + 1 }))
  }

  const frame = {
    border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden',
    background: T.bg, display: 'flex', flexDirection: 'column' as const,
    ...(height ? { height } : { minHeight: 360 }),
  }

  if (done) {
    const ok = done.outcome === 'completed'
    const waiting = done.outcome === 'pending_approval' || done.outcome === 'pending_assignee'
    const Icon = ok ? CheckCircle2 : waiting ? Clock : XCircle
    return (
      <div {...tw('msg', ok ? 'success' : 'warning', { 'wizard-outcome': done.outcome })}
        style={{ ...frame, minHeight: height ? undefined : 200, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
        <Icon size={32} color={ok ? '#136c3a' : waiting ? T.accent : T.muted} aria-hidden />
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>
          {ok ? (successMessage || DONE_TEXT.completed) : DONE_TEXT[done.outcome]}
        </div>
        <button type="button" onClick={restart} style={button(false)}
          {...tw('action', 'wizard-restart')}>
          <RotateCcw size={15} aria-hidden /> Start again
        </button>
      </div>
    )
  }

  return (
    <div style={frame} key={`${launchKey}:${run.n}`}>
      {renderRunner({ wizard, inputs: launchInputs, onClose })}
    </div>
  )
}
